import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider } from "@clerk/clerk-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";
import App from "./App";
import { TenantProvider } from "./contexts/TenantContext";
import { SearchSessionProvider } from "./context/SearchSessionContext";
import { AccessTokenProvider } from "./context/AccessTokenContext";
import { resolvedTenantId } from "./lib/resolveTenant";
import { prefetchAccessDevSdk } from "./hooks/useTravelSdk";
import "./index.css";

type WindowWithIdleCallback = Window & {
  requestIdleCallback?: (
    callback: IdleRequestCallback,
    options?: IdleRequestOptions
  ) => number;
  __waypointDeferredClerkInstalled?: boolean;
  __waypointLoadDeferredClerk?: () => void;
};

const SIGN_IN_CLERK_DELAY_MS = 6500;

const runAfterInitialLoad = (
  task: () => void,
  timeout = 3000,
  minimumDelay = 1200
): void => {
  if (typeof window === "undefined") return;

  window.setTimeout(() => {
    const { requestIdleCallback } = window as WindowWithIdleCallback;
    if (requestIdleCallback) {
      requestIdleCallback(task, { timeout });
      return;
    }
    window.setTimeout(task, Math.min(timeout, 1500));
  }, minimumDelay);
};

const installSignInClerkDeferral = (): void => {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined" ||
    !window.location.pathname.startsWith("/auth/signin") ||
    !document.body
  ) {
    return;
  }

  const win = window as WindowWithIdleCallback;
  if (win.__waypointDeferredClerkInstalled) return;
  win.__waypointDeferredClerkInstalled = true;

  const originalAppendChild = HTMLBodyElement.prototype.appendChild;
  let heldScript: HTMLScriptElement | null = null;
  let restored = false;

  const isClerkScript = (node: Node): node is HTMLScriptElement =>
    node instanceof HTMLScriptElement &&
    node.src.includes("/npm/@clerk/clerk-js@");

  const flush = () => {
    if (restored) return;

    restored = true;
    HTMLBodyElement.prototype.appendChild = originalAppendChild;
    const script = heldScript;
    heldScript = null;

    if (script && !script.isConnected && document.body) {
      originalAppendChild.call(document.body, script);
    }
  };

  HTMLBodyElement.prototype.appendChild = function appendChild<T extends Node>(node: T): T {
    if (this === document.body && isClerkScript(node)) {
      heldScript = node;
      return node;
    }

    return originalAppendChild.call(this, node) as T;
  };

  win.__waypointLoadDeferredClerk = flush;

  window.setTimeout(() => {
    if (win.requestIdleCallback) {
      win.requestIdleCallback(flush, { timeout: 2000 });
      return;
    }

    flush();
  }, SIGN_IN_CLERK_DELAY_MS);
};

const disableServiceWorker = (): void => {
  if (
    import.meta.env.DEV ||
    typeof window === "undefined" ||
    typeof navigator === "undefined"
  ) {
    return;
  }

  if ("serviceWorker" in navigator) {
    void navigator.serviceWorker
      .getRegistrations()
      .then((registrations) =>
        Promise.all(registrations.map((registration) => registration.unregister()))
      )
      .catch((error) => {
        console.warn("[ServiceWorker] Unregister failed", error);
      });
  }

  if ("caches" in window) {
    void caches
      .keys()
      .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
      .catch((error) => {
        console.warn("[ServiceWorker] Cache cleanup failed", error);
      });
  }
};

// Dev-only cache hygiene to prevent stale PWA/api/image caches from previous runs.
if (import.meta.env.DEV && typeof window !== "undefined") {
  if ("serviceWorker" in navigator) {
    void navigator.serviceWorker
      .getRegistrations()
      .then((registrations) =>
        Promise.all(registrations.map((registration) => registration.unregister()))
      )
      .catch(() => undefined);
  }
  if ("caches" in window) {
    void caches
      .keys()
      .then((keys) => Promise.all(keys.map((key) => caches.delete(key))))
      .catch(() => undefined);
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
    },
  },
});

// Clerk publishable keys (all tenants included at build time, selected at runtime)
declare const __CLERK_KEYS__: Record<string, string>;
const sharedClerkPubKey =
  import.meta.env.DEV || import.meta.env.VITE_ALLOW_SHARED_CLERK_KEYS === "true"
    ? import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
    : "";
const clerkPubKey =
  __CLERK_KEYS__[resolvedTenantId] ||
  sharedClerkPubKey ||
  "";
const bypassMissingClerkForLocalSmoke =
  import.meta.env.DEV && import.meta.env.VITE_SCRAPE_AUTH_BYPASS === "true";
const useHeadlessClerk =
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/auth/signin");

if (!clerkPubKey && !bypassMissingClerkForLocalSmoke) {
  console.error(
    `[Auth] Missing Clerk publishable key for tenant "${resolvedTenantId}". ` +
    "Ensure VITE_CLERK_PUBLISHABLE_KEY_<TENANT> env vars are set at build time. " +
    "Production builds do not use the shared VITE_CLERK_PUBLISHABLE_KEY fallback unless VITE_ALLOW_SHARED_CLERK_KEYS=true."
  );
}

if (!import.meta.env.DEV) {
  installSignInClerkDeferral();
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ClerkProvider
      publishableKey={clerkPubKey}
      __internal_bypassMissingPublishableKey={bypassMissingClerkForLocalSmoke}
      clerkJSVariant={useHeadlessClerk ? "headless" : undefined}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <TenantProvider>
            <AccessTokenProvider>
              <SearchSessionProvider>
                <App />
              </SearchSessionProvider>
            </AccessTokenProvider>
          </TenantProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ClerkProvider>
  </StrictMode>
);

if (!import.meta.env.DEV) {
  runAfterInitialLoad(() => {
    // Keep non-critical network and SDK work out of the first render path.
    prefetchAccessDevSdk();
    disableServiceWorker();
  }, 4000, useHeadlessClerk ? 7000 : 1200);
}
